import { useEffect, useState } from 'react'
import { Box, HStack, Icon, IconProps, Text, useMediaQuery } from '@chakra-ui/react'
import { observer } from 'mobx-react'
import { AuthRoleIdEnum } from 'constants/user'
import {
  MOBILE_BANNER_BG,
  MOBILE_BANNER_FG,
  MOBILE_BANNER_HEIGHT,
  MOBILE_BANNER_HEIGHT_VAR
} from 'constants/mobile-page-template'
import { useStores } from 'hooks/useStores'

const MonitorGlyph = (props: IconProps) => (
  <Icon viewBox="0 0 24 24" {...props}>
    <path
      fill="currentColor"
      d="M4 5h16a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-6v2h3v2H7v-2h3v-2H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm1 2v7h14V7H5Z"
    />
  </Icon>
)

/**
 * Dismissal lasts the browsing session and no longer.
 *
 * A coach who closes the advisory has heard it for this sitting; remembering the
 * choice forever would mean a coach who once tapped the ✕ never learns that the
 * page they are fighting with on a phone is a desktop page.
 */
const DISMISSED_KEY = 'rallypd:better-on-desktop-dismissed'

const readDismissed = (): boolean => {
  try {
    return window.sessionStorage.getItem(DISMISSED_KEY) === 'true'
  } catch {
    // Private windows and locked-down browsers throw on access; an advisory is
    // not worth failing the page over, so treat storage as simply unavailable.
    return false
  }
}

const rememberDismissed = (): void => {
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, 'true')
  } catch {
    // Same as above — the banner still closes for this render either way.
  }
}

/**
 * The amber advisory pinned above the RallyPD mobile chrome: coaching is desktop
 * work, and a phone can only carry part of it.
 *
 * Shown to coaches and above only — a player's pages are designed for a phone
 * and have nothing better waiting on a desktop. It is fixed to the top of the
 * viewport and publishes its own height as `--mobile-banner-h`, which is how the
 * fixed topbar and the scroll container below know to step down by it; when the
 * bar is absent the property is too, and the offsets fall back to zero.
 */
const BetterOnDesktopBanner = observer(() => {
  const { authStore } = useStores()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const [isDismissed, setIsDismissed] = useState<boolean>(readDismissed)

  const isVisible = isMobile && !isDismissed && authStore.hasRoleAtLeast(AuthRoleIdEnum.COACH)

  useEffect(() => {
    if (!isVisible) return
    document.body.style.setProperty(MOBILE_BANNER_HEIGHT_VAR, MOBILE_BANNER_HEIGHT)
    return () => {
      document.body.style.removeProperty(MOBILE_BANNER_HEIGHT_VAR)
    }
  }, [isVisible])

  if (!isVisible) return null

  const handleDismiss = () => {
    rememberDismissed()
    setIsDismissed(true)
  }

  return (
    <HStack
      position="fixed"
      top={0}
      left={0}
      right={0}
      // Above the topbar (1001) and the sidebar (1002): the advisory is what the
      // rest of the chrome is offset by, so nothing may cover it.
      zIndex={1003}
      height={MOBILE_BANNER_HEIGHT}
      px="16px"
      spacing={2}
      justify="space-between"
      bg={MOBILE_BANNER_BG}
      borderBottom="0.681px solid rgba(255, 255, 255, 0.10)"
    >
      <HStack spacing={2} minW={0}>
        <MonitorGlyph boxSize="16px" color={MOBILE_BANNER_FG} flexShrink={0} />
        <Text color={MOBILE_BANNER_FG} fontSize="14px" fontWeight={700} lineHeight="20px" m={0} noOfLines={1}>
          Better on desktop
        </Text>
      </HStack>

      <Box
        as="button"
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width="24px"
        height="24px"
        padding={0}
        border="none"
        borderRadius="4px"
        background="transparent"
        color={MOBILE_BANNER_FG}
        cursor="pointer"
        _hover={{ background: 'rgba(0, 0, 0, 0.10)' }}
        _focus={{ boxShadow: 'none', outline: 'none' }}
        _focusVisible={{ boxShadow: 'none', outline: 'none' }}
        sx={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Icon viewBox="0 0 24 24" boxSize="16px">
          <path
            fill="currentColor"
            d="M18.3 5.71 12 12.01l-6.3-6.3-1.41 1.41 6.3 6.3-6.3 6.3 1.41 1.41 6.3-6.3 6.3 6.3 1.41-1.41-6.3-6.3 6.3-6.3z"
          />
        </Icon>
      </Box>
    </HStack>
  )
})

export default BetterOnDesktopBanner
